import {
  applySysPricesToRows,
  fetchPricesForItems,
  type DemPriceRow,
} from './demPoPricing';
import { parseDeliveryDateDmy } from './demDeliveryDate';
import { enrichReviewRowsWithCustItemBlocks } from './demCustItemBlockList';
import { formatDemCustomerAddressDisplay } from './demCustomers';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import {
  createEmptyTemplateConfig,
  fetchDemTemplate,
  fetchItemDetails,
  normalizeMappingEntries,
  saveDemTemplateConfig,
} from './demSkuMap';
import type {
  DemCustomer,
  DemPoExtractHeader,
  DemPoExtractOrder,
  DemPoReviewRow,
  DemTemplateConfig,
} from '../types/dem';

export type ItemLookupMatch = {
  code: string;
  factor: string;
  origin: string;
  uom?: string;
};

export type PoMappingContext = {
  customers: DemCustomer[];
  itemCodes: Set<string>;
  itemOptions: DemPoItemOption[];
  templates: Map<string, DemTemplateConfig>;
  aiCustomerMappings: Map<string, string>;
};

export type DemPoItemOption = {
  code: string;
  description: string;
  is_blocked: boolean;
  uom: string;
  packing: string;
  inventory: number;
};

function normalizeText(text: string): string {
  return text.replace(/[\W_]+/g, '').toLowerCase();
}

function extractPostalsFromText(text: string): string[] {
  return [...new Set(text.match(/\b\d{6}\b/g) ?? [])];
}

const CUSTOMER_MATCH_STOPWORDS = new Set([
  'pte',
  'ltd',
  'p/l',
  'co',
  'inc',
  'the',
  'and',
  'sdn',
  'bhd',
  'road',
  'street',
  'st',
  'rd',
  'ave',
  'avenue',
  'singapore',
  'block',
  'floor',
  'private',
  'limited',
  'shop',
  'store',
  'unit',
  'level',
]);

function customerMatchKeywords(text: string): string[] {
  return (
    text
      .toLowerCase()
      .match(/[a-zA-Z0-9]+/g)
      ?.filter((w) => w.length >= 2 && !CUSTOMER_MATCH_STOPWORDS.has(w)) ?? []
  );
}

/** "RE&S @ Ichiban Boshi - Novena Square" → "Ichiban Boshi - Novena Square" */
function extractOutletLabel(companyName: string): string {
  const raw = companyName.trim();
  if (!raw.includes('@')) return '';
  return raw.split('@').slice(1).join('@').trim();
}

function isLikelySharedHqAddress(address: string): boolean {
  const normalized = address.trim().toLowerCase();
  if (!normalized) return false;
  if (/tai\s*seng/.test(normalized) && /re\s*&?\s*s/.test(normalized)) return true;
  if (/#?\s*07-01/.test(normalized) && /re\s*&?\s*s\s*building/.test(normalized)) return true;
  return false;
}

/** ISS ECAT POs share Harbourfront HQ on every form — match by delivery site instead. */
function isIssSharedHqAddress(address: string): boolean {
  const normalized = address.trim().toLowerCase();
  if (!normalized) return false;
  if (/harbourfront/.test(normalized) && /098633/.test(normalized)) return true;
  if (/1\s*harbourfront\s*place/.test(normalized)) return true;
  return false;
}

function isOutletCustomerCode(code: string): boolean {
  return /-[Ss]\d+[A-Za-z]?$/.test(code.trim()) || /-\d+$/.test(code.trim());
}

export type DemCustomerCodeHint = {
  /** Normalized code without trailing *. */
  code: string;
  /**
   * True when the chat title used a trailing * (e.g. C0123*).
   * Informational only — mother codes with or without * both scope as a family.
   */
  familyWildcard: boolean;
};

/**
 * Pull DEM-style customer codes from chat titles / extract text.
 * Examples:
 * - N0458-S16, E0100*, P0030-S2, C0123
 * - Multi-outlet shorthand: C0123-S1/S3, C0123-S13/15
 */
export function extractDemCustomerCodeHintsFromText(text: string): DemCustomerCodeHint[] {
  const out: DemCustomerCodeHint[] = [];
  const seen = new Set<string>();
  const coveredRanges: Array<[number, number]> = [];

  const add = (code: string, familyWildcard: boolean) => {
    const normalized = code.trim().toUpperCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push({ code: normalized, familyWildcard });
  };

  /** SG addresses often write postal as S467360 — not a DEM code (S0366 is). */
  const isSingaporeSPostal = (index: number, code: string): boolean => {
    const slice = text.slice(index, index + 8);
    if (/^S\d{6}(?:\b|$)/i.test(slice)) return true;
    if (/^S\d{5}$/i.test(code)) {
      const next = text[index + code.length];
      if (next && /\d/.test(next)) return true;
    }
    return false;
  };

  // C0123-S1/S3 or C0123-S13/15 (second part may omit the S letter).
  const multiRe =
    /\b([A-Za-z]\d{3,5})-([A-Za-z]?\d+[A-Za-z]?(?:\s*\/\s*[A-Za-z]?\d+[A-Za-z]?)+)\b/g;
  for (const match of text.matchAll(multiRe)) {
    const mother = String(match[1] ?? '').toUpperCase();
    const parts = String(match[2] ?? '').split(/\s*\/\s*/);
    const first = parts[0] ?? '';
    const letterPrefix = /^([A-Za-z]+)/.exec(first)?.[1] ?? '';
    for (const raw of parts) {
      let suffix = raw.trim();
      if (!suffix) continue;
      if (letterPrefix && /^\d+[A-Za-z]?$/i.test(suffix)) {
        suffix = `${letterPrefix}${suffix}`;
      }
      add(`${mother}-${suffix.toUpperCase()}`, false);
    }
    if (typeof match.index === 'number') {
      coveredRanges.push([match.index, match.index + match[0].length]);
    }
  }

  const singleRe = /\b([A-Za-z]\d{3,5}(?:-[A-Za-z]?\d{1,4}[A-Za-z]?)?)(\*)?/g;
  for (const match of text.matchAll(singleRe)) {
    const index = match.index ?? 0;
    if (coveredRanges.some(([start, end]) => index >= start && index < end)) continue;
    const code = String(match[1] ?? '')
      .trim()
      .toUpperCase();
    if (!code) continue;
    if (isSingaporeSPostal(index, code)) continue;
    add(code, Boolean(match[2]));
  }
  return out;
}

/** @deprecated use extractDemCustomerCodeHintsFromText */
export function extractDemCustomerCodesFromText(text: string): string[] {
  return extractDemCustomerCodeHintsFromText(text).map((h) => h.code);
}

function findCustomerByCode(
  customers: DemCustomer[],
  code: string,
): DemCustomer | undefined {
  const needle = code.trim().toUpperCase();
  if (!needle) return undefined;
  return customers.find((c) => c.code.trim().toUpperCase() === needle);
}

function customerCodeFamilyPrefix(code: string): string {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return '';
  return trimmed.includes('-') ? trimmed.split('-')[0]! : trimmed;
}

function customerInCodeFamily(code: string, familyPrefix: string): boolean {
  const c = code.trim().toUpperCase();
  const p = familyPrefix.trim().toUpperCase();
  if (!c || !p) return false;
  return c === p || c.startsWith(`${p}-`);
}

function pickPreferredCustomer(candidates: DemCustomer[]): DemCustomer | null {
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) => {
    const block = Number(a.is_blocked) - Number(b.is_blocked);
    if (block !== 0) return block;
    const outlet = Number(isOutletCustomerCode(b.code)) - Number(isOutletCustomerCode(a.code));
    if (outlet !== 0) return outlet;
    return b.code.length - a.code.length;
  });
  return sorted[0] ?? null;
}

type TitleCodeResolution =
  | { kind: 'exact'; customer: DemCustomer }
  /** WA mother group C0123 / C0123* — fuzzy only inside this family. */
  | { kind: 'family'; prefixes: string[] }
  /** Slash list C0123-S1/S3 — fuzzy only among these outlets. */
  | { kind: 'outlets'; codes: string[] };

/**
 * Title codes:
 * - N0458-S16 → exact outlet
 * - C0123-S1/S3 or C0123-S13/15 → only those outlets
 * - C0123 / C0123* → family filter; outlets may differ in-chat
 */
function resolveTitleCustomerHint(
  ctx: PoMappingContext,
  extractedStr: string,
): TitleCodeResolution | null {
  const hints = extractDemCustomerCodeHintsFromText(extractedStr);
  if (hints.length === 0) return null;

  const outletCodes: string[] = [];
  const familyPrefixes: string[] = [];

  for (const hint of hints) {
    // Outlet codes (N0458-S16, C0123-S1). Mother codes (C0123 / C0123*) scope the family.
    if (isOutletCustomerCode(hint.code)) {
      outletCodes.push(hint.code.trim().toUpperCase());
      continue;
    }
    familyPrefixes.push(customerCodeFamilyPrefix(hint.code));
  }

  const uniqueOutlets = [...new Set(outletCodes)];
  // C0123-S1/S3 or C0123-S13/15 → keep the whole slash set (fuzzy only among them).
  if (uniqueOutlets.length > 1) {
    return { kind: 'outlets', codes: uniqueOutlets };
  }
  if (uniqueOutlets.length === 1) {
    const hit = findCustomerByCode(ctx.customers, uniqueOutlets[0]!);
    if (hit) return { kind: 'exact', customer: hit };
    return { kind: 'outlets', codes: uniqueOutlets };
  }

  const prefixes = [...new Set(familyPrefixes.map((p) => p.trim().toUpperCase()).filter(Boolean))];
  if (prefixes.length > 0) return { kind: 'family', prefixes };

  return null;
}

export function lookupCustomerAuto(
  ctx: PoMappingContext,
  extractedStr: string,
): { code: string; name: string; address: string } {
  const targetKey = extractedStr.trim();
  if (!targetKey) return { code: '', name: '', address: '' };

  const displayAddress = (customer: DemCustomer | undefined) =>
    customer ? formatDemCustomerAddressDisplay(customer) : '';

  const resultForCustomer = (customer: DemCustomer) => ({
    code: customer.code,
    name: customer.name,
    address: displayAddress(customer),
  });

  const resultForCode = (code: string) => {
    const customer = ctx.customers.find((c) => c.code === code);
    return {
      code,
      name: customer?.name ?? '',
      address: displayAddress(customer),
    };
  };

  // WhatsApp titles: outlet code wins; C0123* only scopes fuzzy match to that family.
  const titleHint = resolveTitleCustomerHint(ctx, targetKey);
  if (titleHint?.kind === 'exact') {
    return resultForCustomer(titleHint.customer);
  }
  const familyPrefixes = titleHint?.kind === 'family' ? titleHint.prefixes : [];
  const outletCodeSet =
    titleHint?.kind === 'outlets'
      ? new Set(titleHint.codes.map((c) => c.trim().toUpperCase()))
      : null;
  let customersForFuzzy = outletCodeSet
    ? ctx.customers.filter((c) => outletCodeSet.has(c.code.trim().toUpperCase()))
    : familyPrefixes.length > 0
      ? ctx.customers.filter((c) =>
          familyPrefixes.some((p) => customerInCodeFamily(c.code, p)),
        )
      : ctx.customers;
  // Bad title hints (e.g. S467360 postal misread as code) must not empty the search set.
  if (customersForFuzzy.length === 0 && (outletCodeSet || familyPrefixes.length > 0)) {
    customersForFuzzy = ctx.customers;
  }

  const exact = ctx.aiCustomerMappings.get(targetKey);
  const targetPostals = extractPostalsFromText(targetKey);
  if (exact) {
    const customer = ctx.customers.find((c) => c.code === exact);
    const outsideFamily =
      customer &&
      familyPrefixes.length > 0 &&
      !familyPrefixes.some((p) => customerInCodeFamily(customer.code, p));
    const outsideOutlets =
      customer && outletCodeSet && !outletCodeSet.has(customer.code.trim().toUpperCase());
    // When scoped to a WA mother/outlet list, ignore memories that point outside it.
    if (outsideFamily || outsideOutlets) {
      // fall through
    } else {
      const mappedAddr = displayAddress(customer);
      const mappedPostals = [
        ...extractPostalsFromText(mappedAddr),
        ...(customer?.postal ? [customer.postal] : []),
      ];
      const postalConflict =
        targetPostals.length > 0 &&
        mappedPostals.length > 0 &&
        !targetPostals.some((p) => mappedPostals.includes(p));
      if (!postalConflict && customer) return resultForCustomer(customer);
      if (!postalConflict && exact) return resultForCode(exact);
    }
  }

  const targetNorm = normalizeText(targetKey);

  // Fuzzy historical map — require strong overlap so a short "ISS Catering…" key
  // cannot steal every ISS PO (including different outlets / addresses).
  if (targetNorm.length > 10) {
    let bestMemCode = '';
    let bestMemScore = 0;
    for (const [memKey, memCode] of ctx.aiCustomerMappings.entries()) {
      if (
        familyPrefixes.length > 0 &&
        !familyPrefixes.some((p) => customerInCodeFamily(memCode, p))
      ) {
        continue;
      }
      if (outletCodeSet && !outletCodeSet.has(memCode.trim().toUpperCase())) {
        continue;
      }
      const memNorm = normalizeText(memKey);
      if (memNorm.length < 12) continue;

      const shorter = memNorm.length <= targetNorm.length ? memNorm : targetNorm;
      const longer = memNorm.length <= targetNorm.length ? targetNorm : memNorm;
      if (!longer.includes(shorter)) continue;

      const coverage = shorter.length / Math.max(longer.length, 1);
      const memPostals = extractPostalsFromText(memKey);
      const postalHit = targetPostals.some((p) => memPostals.includes(p));
      const postalConflict =
        targetPostals.length > 0 &&
        memPostals.length > 0 &&
        !postalHit;
      if (postalConflict) continue;

      // Company-name-only memories must nearly equal the extract; otherwise need shared postal.
      if (coverage < 0.9 && !postalHit) continue;
      if (coverage < 0.55) continue;
      // Do not reuse a tagged outlet when the new document has a different delivery postal.
      if (targetPostals.length > 0 && memPostals.length === 0 && coverage < 0.92) continue;

      const score = coverage * 100 + (postalHit ? 40 : 0) + Math.min(memNorm.length, 80) * 0.05;
      if (score > bestMemScore) {
        bestMemScore = score;
        bestMemCode = memCode;
      }
    }
    if (bestMemCode) return resultForCode(bestMemCode);
  }

  const searchKws = customerMatchKeywords(targetKey);
  if (searchKws.length === 0 && targetPostals.length === 0) {
    // Slash list with a single known outlet → that code; never invent among several.
    if (outletCodeSet) {
      if (customersForFuzzy.length === 1) return resultForCustomer(customersForFuzzy[0]!);
      return { code: '', name: '', address: '' };
    }
    if (familyPrefixes.length > 0) {
      const mother = pickPreferredCustomer(
        familyPrefixes
          .map((p) => findCustomerByCode(ctx.customers, p))
          .filter((c): c is DemCustomer => Boolean(c)),
      );
      if (mother) return resultForCustomer(mother);
    }
    return { code: '', name: '', address: '' };
  }

  const outletLabel = extractOutletLabel(targetKey);
  const outletKws = outletLabel ? customerMatchKeywords(outletLabel) : [];
  // Only ignore postals for RE&S-style "Brand @ Outlet" + shared HQ keys.
  const ignorePostalsForOutlet =
    Boolean(outletLabel) && (isLikelySharedHqAddress(targetKey) || targetPostals.length === 0);
  const usePostals = ignorePostalsForOutlet ? [] : targetPostals;
  const scoreKws = outletKws.length > 0 && ignorePostalsForOutlet ? outletKws : searchKws;

  let bestCode = '';
  let bestScore = 0;
  for (const customer of customersForFuzzy) {
    const addr = formatDemCustomerAddressDisplay(customer);
    const nameLower = customer.name.toLowerCase();
    const addrLower = addr.toLowerCase();
    const dbText = `${customer.code} ${nameLower} ${addrLower}`;
    let score = 0;

    for (const kw of scoreKws) {
      const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\b${escaped}\\b`);
      if (re.test(nameLower)) score += kw.length * 3;
      else if (re.test(dbText)) score += kw.length * 2;
      else if (kw.length > 3 && nameLower.includes(kw)) score += 2;
      else if (kw.length > 3 && dbText.includes(kw)) score += 1;
    }

    // Bonus when the outlet phrase appears in the customer name (RE&S consolidation).
    if (outletLabel && ignorePostalsForOutlet) {
      const outletNorm = normalizeText(outletLabel);
      const nameNorm = normalizeText(customer.name);
      if (outletNorm.length >= 8 && nameNorm.includes(outletNorm)) score += 80;
      else if (outletNorm.length >= 8 && outletNorm.includes(nameNorm) && nameNorm.length >= 8) {
        score += 40;
      }
    }

    for (const postal of usePostals) {
      if (customer.postal === postal || dbText.includes(postal)) score += 60;
    }

    // Prefer outlet / ship-to codes over mother accounts when names score similarly.
    if (isOutletCustomerCode(customer.code)) score += 8;

    if (score > bestScore) {
      bestScore = score;
      bestCode = customer.code;
      continue;
    }

    // Tie-break: same score → prefer postal match, then outlet code (not mother).
    if (score === bestScore && score > 0) {
      const bestCustomer = customersForFuzzy.find((c) => c.code === bestCode);
      const bestHasPostal =
        Boolean(bestCustomer) &&
        usePostals.some(
          (p) =>
            bestCustomer!.postal === p ||
            formatDemCustomerAddressDisplay(bestCustomer!).includes(p),
        );
      const thisHasPostal = usePostals.some(
        (p) => customer.postal === p || dbText.includes(p),
      );
      if (thisHasPostal && !bestHasPostal) {
        bestCode = customer.code;
        continue;
      }
      if (thisHasPostal === bestHasPostal) {
        if (!isOutletCustomerCode(bestCode) && isOutletCustomerCode(customer.code)) {
          bestCode = customer.code;
        }
      }
    }
  }

  // Need a real signal: postal hit alone, or keyword score above threshold.
  const minScore = usePostals.length > 0 ? 40 : 8;
  if (bestScore >= minScore && bestCode) return resultForCode(bestCode);

  // Slash outlets: only auto-pick when exactly one candidate remains in DB.
  if (outletCodeSet) {
    if (customersForFuzzy.length === 1) return resultForCustomer(customersForFuzzy[0]!);
    return { code: '', name: '', address: '' };
  }

  // Family group with no clear outlet signal → fall back to mother account.
  if (familyPrefixes.length > 0) {
    const mother = pickPreferredCustomer(
      familyPrefixes
        .map((p) => findCustomerByCode(ctx.customers, p))
        .filter((c): c is DemCustomer => Boolean(c)),
    );
    if (mother) return resultForCustomer(mother);
  }

  return { code: '', name: '', address: '' };
}

function cleanStr(s: string): string {
  return s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function cleanUom(s: string): string {
  return s.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '').toLowerCase().trim();
}

export function resolveTemplateGroup(customerCode: string): string {
  const code = customerCode.trim();
  if (!code) return 'UNIVERSAL_AI_DETECT';
  if (code.includes('-')) return code.split('-')[0]!.trim();
  return code;
}

async function fetchAllCustomers(): Promise<DemCustomer[]> {
  const db = getDbSupabase('dem');
  const rows: DemCustomer[] = [];
  let offset = 0;
  const pageSize = 1000;

  while (true) {
    const { data, error } = await db
      .from('customers')
      .select(
        'code,name,address,address_2,postal,email,price_group,status,min_order,allowed_days,portal_token,is_blocked,salesperson_code,delivery_instruction,delivery_schedule,bill_to_customer_no',
      )
      .order('code')
      .range(offset, offset + pageSize - 1);
    if (error) throw new Error(formatSupabaseError(error));
    for (const raw of data ?? []) {
      const row = raw as Record<string, unknown>;
      const days = row.allowed_days;
      const address = String(row.address ?? '');
      rows.push({
        code: String(row.code ?? ''),
        name: String(row.name ?? ''),
        address,
        address_2: String(row.address_2 ?? ''),
        postal: String(row.postal ?? '').trim() || (address.match(/\b(\d{6})\b/)?.[1] ?? ''),
        email: String(row.email ?? ''),
        price_group: String(row.price_group ?? ''),
        status: String(row.status ?? ''),
        min_order: Number(row.min_order ?? 0) || 0,
        allowed_days: Array.isArray(days)
          ? days.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6)
          : [],
        portal_token: String(row.portal_token ?? ''),
        is_blocked: Boolean(row.is_blocked),
        salesperson_code: String(row.salesperson_code ?? '').trim(),
        delivery_instruction: String(row.delivery_instruction ?? '').trim(),
        delivery_schedule: String(row.delivery_schedule ?? '').trim(),
        bill_to_customer_no: String(row.bill_to_customer_no ?? '').trim(),
      });
    }
    if ((data ?? []).length < pageSize) break;
    offset += pageSize;
  }

  return rows;
}

async function fetchActiveItemOptions(): Promise<DemPoItemOption[]> {
  const db = getDbSupabase('dem');
  const rows: DemPoItemOption[] = [];
  let offset = 0;
  const pageSize = 1000;

  while (true) {
    const { data, error } = await db
      .from('items')
      .select('code,description,is_blocked,uom,packing,inventory')
      .eq('status', 'Active')
      .not('code', 'ilike', 'AP-%')
      .order('code')
      .range(offset, offset + pageSize - 1);
    if (error) throw new Error(formatSupabaseError(error));
    const chunk = data ?? [];
    for (const row of chunk) {
      const raw = row as Record<string, unknown>;
      const code = String(raw.code ?? '').trim();
      if (!code) continue;
      const inventoryRaw = raw.inventory;
      let inventory = 0;
      if (typeof inventoryRaw === 'number') inventory = inventoryRaw;
      else if (inventoryRaw != null) {
        const parsed = Number(inventoryRaw);
        inventory = Number.isFinite(parsed) ? parsed : 0;
      }
      rows.push({
        code,
        description: String(raw.description ?? ''),
        is_blocked: Boolean(raw.is_blocked),
        uom: String(raw.uom ?? ''),
        packing: String(raw.packing ?? ''),
        inventory,
      });
    }
    if (chunk.length < pageSize) break;
    offset += pageSize;
  }

  return rows;
}

async function fetchAiCustomerMappings(): Promise<Map<string, string>> {
  const db = getDbSupabase('dem');
  const map = new Map<string, string>();
  let offset = 0;
  const pageSize = 1000;

  while (true) {
    const { data, error } = await db
      .from('ai_customer_mappings')
      .select('pdf_text,system_code')
      .range(offset, offset + pageSize - 1);
    if (error) throw new Error(formatSupabaseError(error));
    const chunk = data ?? [];
    for (const row of chunk) {
      const pdfText = String((row as { pdf_text?: string }).pdf_text ?? '').trim();
      const systemCode = String((row as { system_code?: string }).system_code ?? '').trim();
      if (pdfText && systemCode) map.set(pdfText, systemCode);
    }
    if (chunk.length < pageSize) break;
    offset += pageSize;
  }

  return map;
}

async function ensureTemplate(group: string, cache: Map<string, DemTemplateConfig>): Promise<DemTemplateConfig> {
  if (cache.has(group)) return cache.get(group)!;
  const existing = await fetchDemTemplate(group);
  const config = existing?.config ?? createEmptyTemplateConfig(group);
  cache.set(group, config);
  return config;
}

export async function createPoMappingContext(): Promise<PoMappingContext> {
  const [customers, itemOptions, aiCustomerMappings] = await Promise.all([
    fetchAllCustomers(),
    fetchActiveItemOptions(),
    fetchAiCustomerMappings(),
  ]);
  return {
    customers,
    itemCodes: new Set(itemOptions.map((item) => item.code)),
    itemOptions,
    templates: new Map(),
    aiCustomerMappings,
  };
}

let mappingContextPromise: Promise<PoMappingContext> | null = null;

export function prefetchPoMappingContext(): void {
  if (!mappingContextPromise) {
    mappingContextPromise = createPoMappingContext();
  }
}

export function loadPoMappingContext(): Promise<PoMappingContext> {
  if (!mappingContextPromise) {
    mappingContextPromise = createPoMappingContext();
  }
  return mappingContextPromise;
}

function filterMemoryEntries(
  entries: ReturnType<typeof normalizeMappingEntries>,
  targetUom: string,
): ItemLookupMatch[] {
  const targetUomClean = cleanUom(targetUom);
  const groups = new Map<string, ReturnType<typeof normalizeMappingEntries>>();
  for (const entry of entries) {
    const code = entry.code.trim();
    if (!code) continue;
    const list = groups.get(code) ?? [];
    list.push(entry);
    groups.set(code, list);
  }

  const filtered: ItemLookupMatch[] = [];
  for (const [code, groupEntries] of groups) {
    const exact = targetUomClean
      ? groupEntries.filter((e) => cleanUom(e.uom ?? '') === targetUomClean)
      : [];
    if (exact.length > 0) {
      const e = exact[0]!;
      filtered.push({ code, factor: e.factor || '1', origin: 'memory', uom: e.uom });
      continue;
    }
    const emptyUom = groupEntries.filter((e) => !cleanUom(e.uom ?? ''));
    if (emptyUom.length > 0) {
      const e = emptyUom[0]!;
      filtered.push({ code, factor: e.factor || '1', origin: 'memory', uom: e.uom });
    } else if (groupEntries[0]) {
      const e = groupEntries[0];
      filtered.push({ code, factor: e.factor || '1', origin: 'memory', uom: e.uom });
    }
  }
  return filtered;
}

export function lookupItemAutoList(
  ctx: PoMappingContext,
  group: string,
  pdfSku: string,
  pdfDesc: string,
  pdfUom = '',
): ItemLookupMatch[] {
  const pdfSkuClean = String(pdfSku ?? '').trim();
  const pdfDescClean = String(pdfDesc ?? '').trim();
  if (!pdfSkuClean && !pdfDescClean) return [{ code: '', factor: '1', origin: 'none' }];

  const cSku = cleanStr(pdfSkuClean);
  const cDesc = cleanStr(pdfDescClean);
  const keys = [cSku, cDesc].filter(Boolean);

  const searchTemplate = (config: DemTemplateConfig | undefined): ItemLookupMatch[] | null => {
    if (!config?.memory?.items) return null;
    for (const target of keys) {
      for (const [memKey, raw] of Object.entries(config.memory.items)) {
        if (cleanStr(memKey) === target) {
          return filterMemoryEntries(normalizeMappingEntries(raw), pdfUom);
        }
      }
    }
    return null;
  };

  const groupConfig = ctx.templates.get(group);
  const fromGroup = searchTemplate(groupConfig);
  if (fromGroup && fromGroup.length > 0) return fromGroup;

  const universal = ctx.templates.get('UNIVERSAL_AI_DETECT') ?? createEmptyTemplateConfig('UNIVERSAL_AI_DETECT');
  const fromUniversal = searchTemplate(universal);
  if (fromUniversal && fromUniversal.length > 0) return fromUniversal;

  if (cSku) {
    for (const code of ctx.itemCodes) {
      if (cleanStr(code) === cSku) return [{ code, factor: '1', origin: 'exact' }];
    }
  }

  const pdfTokens = new Set(
    `${pdfSkuClean} ${pdfDescClean}`.toUpperCase().replace(/[^A-Z0-9]/g, ' ').split(/\s+/).filter(Boolean),
  );
  const ignore = new Set(['CTN', 'PCS', 'PACK', 'BOX', 'SET', 'PKT', 'BALE', 'ROLL', 'ML', 'L', 'KG', 'G', 'OZS', 'OZ']);
  const core = [...pdfTokens].filter((t) => !ignore.has(t));

  let best = '';
  let bestScore = 0;
  for (const code of ctx.itemCodes) {
    const codeTokens = new Set(
      code.toUpperCase().replace(/[^A-Z0-9]/g, ' ').split(/\s+/).filter((t) => t && !ignore.has(t)),
    );
    if (codeTokens.size === 0) continue;
    const allIn = [...codeTokens].every((t) => core.includes(t));
    if (!allIn) continue;
    const score = [...codeTokens].reduce((sum, t) => sum + t.length, 0);
    if (score > bestScore) {
      bestScore = score;
      best = code;
    }
  }

  if (best) return [{ code: best, factor: '1', origin: 'ai' }];
  return [{ code: '', factor: '1', origin: 'none' }];
}

export async function prepareExtractOrder(
  ctx: PoMappingContext,
  order: DemPoExtractOrder,
  options?: { matchExtra?: string },
): Promise<{
  header: DemPoExtractOrder['header'];
  reviewRows: DemPoReviewRow[];
  priceRows: DemPriceRow[];
}> {
  const header = { ...order.header };
  header.delivery_date = parseDeliveryDateDmy(header.delivery_date ?? '');
  // RE&S consolidation sheets put a shared HQ address on every outlet row — match by
  // outlet name only and drop that HQ address from the document fields.
  // Do NOT do this for real ship-to addresses (e.g. SingHealth Tower on SOR PDFs).
  const sharedHq =
    isLikelySharedHqAddress(header.company_address ?? '') ||
    isIssSharedHqAddress(header.company_address ?? '');
  const outletOnly = extractOutletLabel(header.company_name);
  const matchExtra = String(options?.matchExtra ?? '').trim();
  if (sharedHq) {
    header.company_address = '';
    header.extracted_cust_str = [outletOnly || header.company_name, matchExtra]
      .filter(Boolean)
      .join(' ')
      .trim();
  } else {
    header.extracted_cust_str = [header.company_name, header.company_address, matchExtra]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  const customer = lookupCustomerAuto(ctx, header.extracted_cust_str);
  header.sys_customer_code = customer.code;
  header.sys_customer_name = customer.name;
  header.sys_customer_address = formatDemCustomerAddressDisplay(customer);

  const group = resolveTemplateGroup(customer.code);
  await ensureTemplate(group, ctx.templates);
  await ensureTemplate('UNIVERSAL_AI_DETECT', ctx.templates);

  const reviewRows: DemPoReviewRow[] = [];
  const pendingCodes: string[] = [];
  let index = 0;
  for (const row of order.rows) {
    // PDF SKU column = extracted item/material code only. Do not fall back to
    // description (SATS short POs have no PO SKU — leave blank; map via desc separately).
    const pdfKey = String(row.items ?? '').trim();
    const pdfQty = Number(String(row.quantity ?? '').replace(/[^\d.]/g, '')) || 0;
    const matches = lookupItemAutoList(
      ctx,
      group,
      String(row.items ?? ''),
      String(row.description ?? ''),
      String(row.uom ?? ''),
    );
    const mappingList = matches.length > 0 ? matches : [{ code: '', factor: '1', origin: 'none' }];
    const groupId = `grp-${index}`;

    for (let matchIndex = 0; matchIndex < mappingList.length; matchIndex += 1) {
      const match = mappingList[matchIndex]!;
      const factor = Number(match.factor || '1') || 1;
      const sysCode = match.code;
      if (sysCode) pendingCodes.push(sysCode);
      reviewRows.push({
        id: matchIndex === 0 ? `row-${index}` : `row-${index}-m${matchIndex}`,
        groupId,
        isPrimary: matchIndex === 0,
        pdfKey,
        pdfDesc: String(row.description ?? ''),
        pdfQty,
        pdfUom: String(row.uom ?? ''),
        pdfPrice: String(row.unit_price ?? ''),
        sysCode,
        factor: String(match.factor || '1'),
        sysDesc: '',
        sysUom: '',
        sysPacking: '',
        sysBlocked: false,
        sysPrice: '0.000',
        finalQty: Math.round(pdfQty * factor),
      });
    }
    index += 1;
  }

  const itemDetails = await fetchItemDetails(pendingCodes);
  const itemBasePrices: Record<string, number> = {};
  for (const [code, detail] of Object.entries(itemDetails)) {
    itemBasePrices[code] = detail.base_price;
  }
  for (const reviewRow of reviewRows) {
    const code = reviewRow.sysCode.trim();
    if (!code) {
      reviewRow.sysDesc = reviewRow.pdfDesc;
      continue;
    }
    const detail = itemDetails[code];
    if (!detail) {
      reviewRow.sysDesc = reviewRow.pdfDesc;
      continue;
    }
    reviewRow.sysDesc = detail.description || reviewRow.pdfDesc;
    reviewRow.sysUom = detail.uom;
    reviewRow.sysPacking = detail.packing;
    reviewRow.sysBlocked = detail.is_blocked;
  }

  const priceRows = await fetchPricesForItems(pendingCodes);
  const pricedRows = applySysPricesToRows(reviewRows, priceRows, header, ctx.customers, itemBasePrices);
  const withBlocks = customer.code
    ? await enrichReviewRowsWithCustItemBlocks(customer.code, pricedRows)
    : pricedRows;

  return { header, reviewRows: withBlocks, priceRows };
}

export async function reapplyItemsForCustomerChange(
  ctx: PoMappingContext,
  header: DemPoExtractHeader,
  rows: DemPoReviewRow[],
  priceRows: DemPriceRow[],
): Promise<{
  header: DemPoExtractHeader;
  reviewRows: DemPoReviewRow[];
  priceRows: DemPriceRow[];
}> {
  const customerCode = header.sys_customer_code?.trim() ?? '';
  const customer = ctx.customers.find((c) => c.code === customerCode);
  const nextHeader: DemPoExtractHeader = {
    ...header,
    sys_customer_name: customer?.name ?? '',
    sys_customer_address: customer ? formatDemCustomerAddressDisplay(customer) : '',
  };

  const group = resolveTemplateGroup(customerCode);
  await ensureTemplate(group, ctx.templates);
  await ensureTemplate('UNIVERSAL_AI_DETECT', ctx.templates);

  const rematchedRows: DemPoReviewRow[] = [];
  const pendingCodes: string[] = [];
  const primaries = rows.filter((row) => row.isPrimary);

  for (const primary of primaries) {
    const matches = lookupItemAutoList(ctx, group, primary.pdfKey, primary.pdfDesc, primary.pdfUom);
    const mappingList = matches.length > 0 ? matches : [{ code: '', factor: '1', origin: 'none' }];
    const groupId = primary.groupId;
    const existingGroup = rows.filter((row) => row.groupId === groupId);

    for (let matchIndex = 0; matchIndex < mappingList.length; matchIndex += 1) {
      const match = mappingList[matchIndex]!;
      const factor = Number(match.factor || '1') || 1;
      const sysCode = match.code;
      if (sysCode) pendingCodes.push(sysCode);

      const existing = existingGroup[matchIndex];
      rematchedRows.push({
        id: existing?.id ?? (matchIndex === 0 ? primary.id : `${primary.id}-m${matchIndex}`),
        groupId,
        isPrimary: matchIndex === 0,
        pdfKey: primary.pdfKey,
        pdfDesc: primary.pdfDesc,
        pdfQty: primary.pdfQty,
        pdfUom: primary.pdfUom,
        pdfPrice: primary.pdfPrice,
        sysCode,
        factor: String(match.factor || '1'),
        sysDesc: '',
        sysUom: '',
        sysPacking: '',
        sysBlocked: false,
        sysPrice: '0.000',
        finalQty: Math.round(primary.pdfQty * factor),
      });
    }
  }

  const uniqueCodes = [...new Set(pendingCodes.map((code) => code.trim()).filter(Boolean))];
  const itemDetails = await fetchItemDetails(uniqueCodes);
  const itemBasePrices: Record<string, number> = {};
  for (const [code, detail] of Object.entries(itemDetails)) {
    itemBasePrices[code] = detail.base_price;
  }
  for (const reviewRow of rematchedRows) {
    const code = reviewRow.sysCode.trim();
    if (!code) {
      reviewRow.sysDesc = reviewRow.pdfDesc;
      continue;
    }
    const detail = itemDetails[code];
    if (!detail) {
      reviewRow.sysDesc = reviewRow.pdfDesc;
      continue;
    }
    reviewRow.sysDesc = detail.description || reviewRow.pdfDesc;
    reviewRow.sysUom = detail.uom;
    reviewRow.sysPacking = detail.packing;
    reviewRow.sysBlocked = detail.is_blocked;
  }

  const fetchedPrices = await fetchPricesForItems(uniqueCodes);
  const mergedPrices = [
    ...priceRows.filter((row) => !uniqueCodes.includes(row.item.toUpperCase())),
    ...fetchedPrices,
  ];
  const pricedRows = applySysPricesToRows(rematchedRows, mergedPrices, nextHeader, ctx.customers, itemBasePrices);
  const withBlocks = customerCode
    ? await enrichReviewRowsWithCustItemBlocks(customerCode, pricedRows)
    : pricedRows;

  return { header: nextHeader, reviewRows: withBlocks, priceRows: mergedPrices };
}

export async function persistPoMappings(
  ctx: PoMappingContext,
  header: DemPoExtractOrder['header'],
  rows: DemPoReviewRow[],
): Promise<void> {
  const customerCode = header.sys_customer_code?.trim() ?? '';
  const group = resolveTemplateGroup(customerCode);
  const template = await ensureTemplate(group, ctx.templates);

  const nextConfig = structuredClone(template);
  if (!nextConfig.memory) nextConfig.memory = {};
  if (!nextConfig.memory.items) nextConfig.memory.items = {};

  const today = new Date().toISOString().slice(0, 10);

  // Group review rows by PDF key so we can rewrite that key's memory (including ACT deletes).
  const rowsByPdfKey = new Map<string, DemPoReviewRow[]>();
  for (const row of rows) {
    const pdfKey = row.pdfKey.trim();
    if (!pdfKey) continue;
    const list = rowsByPdfKey.get(pdfKey) ?? [];
    list.push(row);
    rowsByPdfKey.set(pdfKey, list);
  }

  for (const [pdfKey, keyRows] of rowsByPdfKey.entries()) {
    // Review rows for this PDF key are the source of truth (including removed ACT lines).
    const fromReview: ReturnType<typeof normalizeMappingEntries> = [];
    const seen = new Set<string>();
    for (const row of keyRows) {
      const sysCode = row.sysCode.trim();
      if (!sysCode) continue;
      if (['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(sysCode.toUpperCase())) continue;
      const uom = row.pdfUom;
      const dedupeKey = `${sysCode.toUpperCase()}|${cleanUom(uom)}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      fromReview.push({
        code: sysCode,
        factor: row.factor || '1',
        uom,
        last_used: today,
      });
    }

    if (fromReview.length === 0) {
      delete nextConfig.memory.items[pdfKey];
    } else {
      nextConfig.memory.items[pdfKey] = fromReview;
    }
  }

  await saveDemTemplateConfig(group, nextConfig);
  ctx.templates.set(group, nextConfig);

  const custKey = header.extracted_cust_str?.trim();
  if (custKey && customerCode) {
    const db = getDbSupabase('dem');
    const { error } = await db.from('ai_customer_mappings').upsert(
      { pdf_text: custKey, system_code: customerCode, last_used: today },
      { onConflict: 'pdf_text' },
    );
    if (error) throw new Error(formatSupabaseError(error));
    ctx.aiCustomerMappings.set(custKey, customerCode);
  }
}

export function rowsToSavePayload(rows: DemPoReviewRow[]): Array<{
  item_code: string;
  description: string;
  qty: number;
  uom: string;
}> {
  return rows
    .filter((row) => {
      const code = row.sysCode.trim().toUpperCase();
      return row.finalQty > 0 && code && !['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(code);
    })
    .map((row) => ({
      item_code: row.sysCode.trim(),
      description: row.sysDesc || row.pdfDesc,
      qty: row.finalQty,
      uom: row.sysUom || row.pdfUom,
    }));
}
